// Public Deposit Account Service for Frontend Classic
// This service only provides masked/limited data for public use
import {
    getConfigurationServiceBaseUrl,
    withConfigurationServiceBaseUrl,
} from '../lib/apiBaseUrl'

export interface PublicDepositAccount {
    _id: string
    accountName: string
    bankName: string
    bankCode: string
    bankIcon: string
    accountType: 'savings' | 'current' | 'business'
    isActive: boolean
    isDefault: boolean
    description?: string
    minDepositAmount?: number | null
    maxDepositAmount?: number | null
    currency: string
    accountNumber: string // Full account number
    depositSourceMode?: DepositAccountSourceMode
}

export type DepositAccountSourceMode = 'both' | 'slip_only' | 'sms_only'

export interface PublicPromptPayAccount {
    _id: string
    accountName: string
    promptPayId: string
    promptPayType: 'phone_number' | 'national_id' | 'tax_id' | 'ewallet' | 'custom'
    description?: string
    minDepositAmount?: number | null
    maxDepositAmount?: number | null
    isActive: boolean
}

export interface DepositAccountResponse {
    success: boolean
    data: PublicDepositAccount[]
    message?: string
    error?: string
}

interface PromptPayAccountResponse {
    success: boolean
    data?: PublicPromptPayAccount[] | null
    message?: string
    error?: string
}

export const isTrueMoneyDepositAccount = (account: Pick<PublicDepositAccount, 'bankCode' | 'bankName'>): boolean => {
    const code = String(account.bankCode || '').toUpperCase()
    const name = String(account.bankName || '').toLowerCase()
    return code === 'TRUE' || name.includes('true money') || name.includes('truemoney')
}

export const isPromptPayDepositAccount = (account: Pick<PublicDepositAccount, 'bankCode' | 'bankName'>): boolean => {
    const code = String(account.bankCode || '').toUpperCase()
    const name = String(account.bankName || '').toLowerCase()
    return code === 'PROMPTPAY' || name.includes('promptpay') || name.includes('prompt pay')
}

export const getDepositAccountSourceMode = (
    account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): DepositAccountSourceMode => {
    const rawMode = String(account?.depositSourceMode || '').trim().toLowerCase()
    if (rawMode === 'slip_only' || rawMode === 'sms_only') {
        return rawMode
    }
    return 'both'
}

export const isSmsOnlyDepositAccount = (
    account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): boolean => getDepositAccountSourceMode(account) === 'sms_only'

export const isSlipOnlyDepositAccount = (
    account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): boolean => getDepositAccountSourceMode(account) === 'slip_only'

const hasConfiguredAmountRange = (account: PublicDepositAccount): boolean =>
    typeof account.minDepositAmount === 'number' && typeof account.maxDepositAmount === 'number'

export const resolveDepositAccountForAmount = (
    accounts: PublicDepositAccount[],
    amount: number,
    options?: { channel?: 'bank' | 'true_money' | 'promptpay' }
): PublicDepositAccount | null => {
    const channel = options?.channel ?? 'bank'
    const candidates = accounts.filter((account) => {
        if (channel === 'true_money') return isTrueMoneyDepositAccount(account)
        if (channel === 'promptpay') return isPromptPayDepositAccount(account)
        return !isTrueMoneyDepositAccount(account) && !isPromptPayDepositAccount(account)
    })

    if (!Number.isFinite(amount) || amount <= 0) {
        return candidates.find((account) => account.isDefault) || candidates[0] || null
    }

    const rangedAccounts = candidates.filter(hasConfiguredAmountRange)
    if (rangedAccounts.length === 0) {
        return candidates.find((account) => account.isDefault) || candidates[0] || null
    }

    return rangedAccounts.find((account) =>
        amount >= Number(account.minDepositAmount) && amount <= Number(account.maxDepositAmount)
    ) || null
}

const DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS = 8000
const DEPOSIT_ACCOUNT_CACHE_TTL_MS = 5 * 60 * 1000
const depositAccountsCache = new Map<string, { value: PublicDepositAccount[]; expiresAt: number }>()

async function fetchWithClientTimeout(input: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    try {
        return await fetch(input, { ...init, signal: controller.signal })
    } finally {
        clearTimeout(timer)
    }
}

export const publicDepositAccountService = {
    // Get active deposit accounts for public use (masked sensitive data)
    async getActiveDepositAccounts(tenantId: string, options?: { forceRefresh?: boolean }): Promise<PublicDepositAccount[]> {
        const cacheKey = String(tenantId || '')
        if (!options?.forceRefresh) {
            const cached = depositAccountsCache.get(cacheKey)
            if (cached && cached.expiresAt > Date.now()) {
                return cached.value
            }
        }

        try {
            const response = await fetchWithClientTimeout(
                withConfigurationServiceBaseUrl(`/deposit-accounts/${tenantId}/public`),
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                },
                DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data: DepositAccountResponse = await response.json()
            const accounts = data.success && Array.isArray(data.data) ? data.data : []
            depositAccountsCache.set(cacheKey, {
                value: accounts,
                expiresAt: Date.now() + DEPOSIT_ACCOUNT_CACHE_TTL_MS
            })
            return accounts
        } catch (error) {
            if ((error as Error)?.name === 'AbortError') {
                console.warn('Deposit accounts request timed out, using fallback')
            } else {
                console.error('Error fetching public deposit accounts:', error)
            }
            const cached = depositAccountsCache.get(cacheKey)
            if (cached) {
                return cached.value
            }
            return []
        }
    },

    // Get default deposit account for public use
    async getDefaultDepositAccount(tenantId: string): Promise<PublicDepositAccount | null> {
        try {
            const accounts = await this.getActiveDepositAccounts(tenantId)
            return accounts.find(account => account.isDefault) || accounts[0] || null
        } catch (error) {
            console.error('Error fetching default deposit account:', error)
            return null
        }
    },

    // Get deposit accounts by bank
    async getDepositAccountsByBank(tenantId: string, bankCode: string): Promise<PublicDepositAccount[]> {
        try {
            const accounts = await this.getActiveDepositAccounts(tenantId)
            return accounts.filter(account => account.bankCode === bankCode)
        } catch (error) {
            console.error('Error fetching deposit accounts by bank:', error)
            return []
        }
    },

    // Validate if account exists (without exposing sensitive data)
    async validateAccountExists(tenantId: string, bankCode: string, partialAccountNumber: string): Promise<boolean> {
        try {
            const accounts = await this.getActiveDepositAccounts(tenantId)
            return accounts.some(account =>
                account.bankCode === bankCode &&
                account.accountNumber.includes(partialAccountNumber)
            )
        } catch (error) {
            console.error('Error validating account existence:', error)
            return false
        }
    },
}

export const mapPromptPayAccountToDepositAccount = (
    account: PublicPromptPayAccount
): PublicDepositAccount => ({
    _id: `promptpay:${account._id}`,
    accountName: account.accountName,
    bankName: 'PromptPay',
    bankCode: 'PROMPTPAY',
    bankIcon: '',
    accountType: 'savings',
    isActive: account.isActive,
    isDefault: false,
    description: account.description || '',
    minDepositAmount: account.minDepositAmount ?? null,
    maxDepositAmount: account.maxDepositAmount ?? null,
    currency: 'THB',
    accountNumber: account.promptPayId,
})

export const mapPromptPayAccountsToDepositAccounts = (
    accounts: PublicPromptPayAccount[]
): PublicDepositAccount[] => accounts.map(mapPromptPayAccountToDepositAccount)

const promptPayAccountsCache = new Map<string, { value: PublicPromptPayAccount[]; expiresAt: number }>()

export async function getPublicPromptPayAccounts(tenantId: string, options?: { forceRefresh?: boolean }): Promise<PublicPromptPayAccount[]> {
    const cacheKey = String(tenantId || '')
    if (!options?.forceRefresh) {
        const cached = promptPayAccountsCache.get(cacheKey)
        if (cached && cached.expiresAt > Date.now()) {
            return cached.value
        }
    }

    try {
        const response = await fetchWithClientTimeout(
            withConfigurationServiceBaseUrl(`/promptpay-accounts/${tenantId}/public`),
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            },
            DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
        )

        if (!response.ok) {
            return promptPayAccountsCache.get(cacheKey)?.value || []
        }

        const data: PromptPayAccountResponse = await response.json()
        const accounts = data.success && Array.isArray(data.data) ? data.data : []
        promptPayAccountsCache.set(cacheKey, {
            value: accounts,
            expiresAt: Date.now() + DEPOSIT_ACCOUNT_CACHE_TTL_MS
        })
        return accounts
    } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
            console.warn('PromptPay accounts request timed out, using fallback')
        } else {
            console.error('Error fetching public promptpay accounts:', error)
        }
        return promptPayAccountsCache.get(cacheKey)?.value || []
    }
}

/** ช่องทางฝากจาก Admin (withdrawal settings) — ใช้ซ่อน/แสดงฝากทศนิยม */
export interface DepositChannels {
    bank: boolean
    trueMoney: boolean
    promptPay: boolean
    decimalReservation: boolean
    allowSubmitWithoutSlip: boolean
}

const DEFAULT_DEPOSIT_CHANNELS: DepositChannels = {
    bank: true,
    trueMoney: true,
    promptPay: true,
    decimalReservation: true,
    allowSubmitWithoutSlip: true,
}

const buildWithdrawalSettingsUrl = (baseUrl: string, tenantId: string): string =>
    `${baseUrl}/withdrawal/settings?tenantScope=${encodeURIComponent(tenantId)}&_t=${Date.now()}`

const getOptionalAuthToken = (): string => {
    try {
        return (
            localStorage.getItem('accessToken') ||
            localStorage.getItem('authToken') ||
            localStorage.getItem('token') ||
            sessionStorage.getItem('accessToken') ||
            sessionStorage.getItem('authToken') ||
            sessionStorage.getItem('token') ||
            ''
        )
    } catch {
        return ''
    }
}

const getNoCacheRequestInit = (): RequestInit => ({
    method: 'GET',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0'
    },
    cache: 'no-store'
})

const getWithdrawalSettingsRequestInit = (tenantId: string): RequestInit => {
    const token = getOptionalAuthToken()
    return {
        ...getNoCacheRequestInit(),
        headers: {
            ...((getNoCacheRequestInit().headers || {}) as Record<string, string>),
            'X-Tenant-ID': tenantId,
            ...(typeof window !== 'undefined' && window.location.hostname
                ? { 'X-Tenant-Domain': window.location.hostname }
                : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
    }
}

const DEFAULT_DECIMAL_COUNTDOWN_MINUTES = 5
const DEFAULT_PRESET_AMOUNTS = [100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000]
const MAX_PRESET_AMOUNTS = 12

interface WithdrawalSettingsRaw {
    depositChannels?: Partial<DepositChannels> & {
        decimalReservationCountdownMinutes?: number
        presetAmounts?: number[]
    }
}

const WITHDRAWAL_SETTINGS_CACHE_TTL_MS = 3 * 60 * 1000
const withdrawalSettingsCache = new Map<string, { value: WithdrawalSettingsRaw; expiresAt: number }>()
const withdrawalSettingsInflight = new Map<string, Promise<WithdrawalSettingsRaw>>()

function normalizePresetAmounts(values: unknown): number[] {
    if (!Array.isArray(values)) return DEFAULT_PRESET_AMOUNTS
    const normalized = values
        .map((value) => Number(value))
        .filter((value, index, arr) => Number.isFinite(value) && value > 0 && arr.indexOf(value) === index)
        .slice(0, MAX_PRESET_AMOUNTS)
    return normalized.length > 0 ? normalized : DEFAULT_PRESET_AMOUNTS
}

async function loadWithdrawalSettings(
    tenantId: string,
    options?: { forceRefresh?: boolean }
): Promise<WithdrawalSettingsRaw> {
    const cacheKey = String(tenantId || '')
    if (!options?.forceRefresh) {
        const cached = withdrawalSettingsCache.get(cacheKey)
        if (cached && cached.expiresAt > Date.now()) {
            return cached.value
        }
        const inflight = withdrawalSettingsInflight.get(cacheKey)
        if (inflight) {
            return inflight
        }
    }

    const promise = (async (): Promise<WithdrawalSettingsRaw> => {
        try {
            const res = await fetchWithClientTimeout(
                buildWithdrawalSettingsUrl(getConfigurationServiceBaseUrl(), tenantId),
                getWithdrawalSettingsRequestInit(tenantId),
                DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
            )
            if (!res.ok) {
                return withdrawalSettingsCache.get(cacheKey)?.value || {}
            }
            const data = (await res.json()) as { data?: { settings?: WithdrawalSettingsRaw } }
            const settings = data?.data?.settings || {}
            withdrawalSettingsCache.set(cacheKey, {
                value: settings,
                expiresAt: Date.now() + WITHDRAWAL_SETTINGS_CACHE_TTL_MS
            })
            return settings
        } catch (e) {
            if ((e as Error)?.name === 'AbortError') {
                console.warn('Withdrawal settings request timed out, using fallback')
            } else {
                console.error('Error fetching withdrawal settings:', e)
            }
            return withdrawalSettingsCache.get(cacheKey)?.value || {}
        } finally {
            withdrawalSettingsInflight.delete(cacheKey)
        }
    })()

    withdrawalSettingsInflight.set(cacheKey, promise)
    return promise
}

export async function getDepositChannels(
    tenantId: string,
    options?: { forceRefresh?: boolean }
): Promise<DepositChannels> {
    const settings = await loadWithdrawalSettings(tenantId, options)
    const ch = settings?.depositChannels
    if (!ch || typeof ch !== 'object') return DEFAULT_DEPOSIT_CHANNELS
    return {
        bank: ch.bank !== false,
        trueMoney: ch.trueMoney !== false,
        promptPay: ch.promptPay !== false,
        decimalReservation: ch.decimalReservation !== false,
        allowSubmitWithoutSlip: ch.allowSubmitWithoutSlip !== false,
    }
}

/** เวลา countdown หน้ารอโอนเงินจองทศนิยม (นาที) */
export async function getDecimalReservationCountdownMinutes(
    tenantId: string,
    options?: { forceRefresh?: boolean }
): Promise<number> {
    const settings = await loadWithdrawalSettings(tenantId, options)
    const minutes = settings?.depositChannels?.decimalReservationCountdownMinutes
    if (typeof minutes === 'number' && minutes >= 1 && minutes <= 60) return minutes
    return DEFAULT_DECIMAL_COUNTDOWN_MINUTES
}

export async function getDepositPresetAmounts(
    tenantId: string,
    options?: { forceRefresh?: boolean }
): Promise<number[]> {
    const settings = await loadWithdrawalSettings(tenantId, options)
    return normalizePresetAmounts(settings?.depositChannels?.presetAmounts)
}

export default publicDepositAccountService
